import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/*
 * HANNE BLOCH infinite scroll (rewritten 2026-10-08 for Vic).
 *
 * Why it was rewritten: the earlier custom version capped the grid at 120 cards and deleted cards
 * from the top while scrolling down. Deleted pages were never loaded again, so scrolling back up
 * left the newest products (page 1) missing, and every deletion made the page jump. The URL also
 * said "page=<last page loaded>", not the page being looked at, and two loads could wait on the
 * same promise and stall. The cap gave no real speed: the whole store is about 140 products and
 * card images are lazy loaded.
 *
 * How it works now:
 *   1. Scrolling down: the next page is prefetched in the background and added about 1200px
 *      before the end of the grid, so the shopper never waits. Nothing is ever removed.
 *   2. The address bar follows the page the shopper is looking at (page=N), and drops page= at
 *      the top. Only so a refresh or the back button lands in the same place.
 *   3. Coming back (back button or refresh on page=N): the card the shopper last saw or clicked
 *      is put back where it was, and the two pages above it are added in one step without moving
 *      the screen. Further pages up load ahead of the shopper (same 1200px margin) and are only
 *      inserted while the page is at rest, so a flick on a phone is never interrupted. Built for
 *      large collections too (the Moving Sale adds 200+ products): nothing loads that the
 *      shopper does not scroll towards.
 *   4. Each page is fetched once (one shared request per page); a failed load retries on its own.
 *   5. (2026-10-09 evening, Vic's phone test: cards arriving "at the last second", a laggy scroll in Low
 *      Power Mode) The next page is added about 2000px before the end of the grid, two pages are fetched
 *      ahead, and a fetched page is parsed into cards while the browser is idle, so adding it is one cheap
 *      append instead of a parse of 400 KB of HTML in the middle of a flick.
 */

const RESTORE_KEY = 'hb-plp-restore';
const RESTORE_PAGES_ABOVE = 2; // pages added at once above the restored page
const SCROLL_REST_MS = 150; // "at rest" = no scroll event for this long
const LOAD_AHEAD = '2000px 0px';
const URL_LINE = 0.4; // the card crossing 40% of the viewport height counts as "the page in view"
const INTERACTIVE =
  'button, input, label, select, [tabindex="1"], card-swatch, .atc-icon-wrapper, .variant-popup, .variant-popup-inner, .custom-size-row, .atc-plus-icon';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /** @type {Map<number, string>} page number -> section HTML */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** Kept for compatibility with code that reads them. */
  renderingPages = new Set();
  fetchingPages = new Set();
  renderedPages = new Set();
  initialRendered = false;
  isInitialRendering = false;

  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;

  /** @type {Map<number, Promise<string | undefined>>} */
  #requests = new Map();

  /** @type {Map<number, Element[]>} page number -> cards parsed while idle, ready to append */
  #parsed = new Map();

  #first = 1;
  #last = 1;
  #urlPage = 1;
  #gen = 0;
  #busyNext = false;
  #busyPrevious = false;
  #scrollTimer = 0;
  #lastScrollAt = 0;
  #clickSavedAt = 0;

  /** @type {{ el: Element, top: number } | null} */
  #anchor = null;
  #userMoved = false;

  connectedCallback() {
    super.connectedCallback();

    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
    }

    this.#resetRange();

    if (!this.#designMode && 'scrollRestoration' in history) {
      // We put the shopper back ourselves (see #restorePosition); the browser's own guess uses an
      // old scroll height and lands in the wrong place on a page that loads its content later.
      history.scrollRestoration = 'manual';
    }

    this.#restorePosition();
    this.#initialRender();
    this.#prefetch(this.#last + 1);
    this.#prefetch(this.#last + 2);
    this.#observeViewMore();

    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('pagehide', this.#onPageHide);
    this.addEventListener('click', this.#onCardClick, true);
    for (const type of ['wheel', 'touchstart', 'keydown']) {
      window.addEventListener(type, this.#onUserMove, { passive: true, once: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.infinityScrollObserver?.disconnect();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('pagehide', this.#onPageHide);
    this.removeEventListener('click', this.#onCardClick, true);
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  get #designMode() {
    return Boolean(window.Shopify?.designMode);
  }

  get #lastPageNumber() {
    return Number(this.refs.grid?.dataset.lastPage) || 1;
  }

  #getCurrentPage() {
    const url = new URL(window.location.href);
    return Number(url.searchParams.get('page')) || 1;
  }

  #resetRange() {
    const page = this.#getCurrentPage();
    this.#first = page;
    this.#last = page;
    this.#urlPage = page;
    this.renderedPages.clear();
    this.renderedPages.add(page);
  }

  /** @returns {HTMLElement[]} */
  #cards() {
    const { grid } = this.refs;
    if (!grid) return [];
    return /** @type {HTMLElement[]} */ ([...grid.querySelectorAll(':scope > [ref="cards[]"]')]);
  }

  // ---------------------------------------------------------------- loading

  /** @param {number} page */
  #pageUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    return url;
  }

  /**
   * One shared request per page. Resolves to the section HTML, or undefined when the page does
   * not exist or the request failed (a failed request is forgotten so it can be tried again).
   * @param {number} page
   * @returns {Promise<string | undefined>}
   */
  #loadPage(page) {
    if (page < 1 || page > this.#lastPageNumber) return Promise.resolve(undefined);
    const cached = this.pages.get(page);
    if (cached) return Promise.resolve(cached);

    const pending = this.#requests.get(page);
    if (pending) return pending;

    const gen = this.#gen;
    this.fetchingPages.add(page);
    const request = sectionRenderer
      .getSectionHTML(this.sectionId, true, this.#pageUrl(page))
      .then((html) => {
        if (gen === this.#gen && html) this.pages.set(page, html);
        return html || undefined;
      })
      .catch(() => undefined)
      .finally(() => {
        if (this.#requests.get(page) === request) this.#requests.delete(page);
        this.fetchingPages.delete(page);
      });

    this.#requests.set(page, request);
    return request;
  }

  /** @param {number} page */
  #prefetch(page) {
    if (page < 1 || page > this.#lastPageNumber || this.renderedPages.has(page)) return;
    const gen = this.#gen;
    requestIdleCallback(() => {
      this.#loadPage(page).then((html) => {
        if (!html || gen !== this.#gen || this.#parsed.has(page) || this.renderedPages.has(page)) return;
        // parse while idle too, so the append later is immediate
        requestIdleCallback(() => {
          if (gen !== this.#gen || this.#parsed.has(page) || this.renderedPages.has(page)) return;
          this.#parsed.set(page, this.#cardsFromHTML(html));
        });
      });
    });
  }

  /**
   * The page's cards: parsed earlier while idle when possible, otherwise now.
   * @param {number} page
   * @param {string} html
   * @returns {Element[]}
   */
  #cardsFor(page, html) {
    const ready = this.#parsed.get(page);
    if (ready) {
      this.#parsed.delete(page);
      return ready;
    }
    return this.#cardsFromHTML(html);
  }

  /**
   * @param {string} html
   * @returns {Element[]}
   */
  #cardsFromHTML(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const grid = parsed.querySelector('[ref="grid"]');
    if (!grid) return [];
    return [...grid.querySelectorAll(':scope > [ref="cards[]"]')];
  }

  // ---------------------------------------------------------------- infinite scroll

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    if (!viewMorePrevious && !viewMoreNext) return;

    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const { viewMorePrevious, viewMoreNext } = this.refs;
            // The entry can be stale (e.g. the screen was just moved back to a product), so check
            // the trigger is really still within range before loading.
            if (!this.#isNear(entry.target)) continue;
            if (entry.target === viewMoreNext) this.#renderNextPage();
            else if (entry.target === viewMorePrevious) this.#renderPreviousPage();
          }
        },
        { rootMargin: LOAD_AHEAD }
      );
    }

    if (viewMorePrevious) this.infinityScrollObserver.observe(viewMorePrevious);
    if (viewMoreNext) this.infinityScrollObserver.observe(viewMoreNext);
  }

  /** @param {Element} el */
  #isNear(el) {
    const rect = el.getBoundingClientRect();
    return rect.bottom > -2100 && rect.top < window.innerHeight + 2100;
  }

  /**
   * Re-observing makes the observer report again, so a sentinel that is still in range after a
   * page was added (a short page, a fast scroll) loads the following page too.
   * @param {'next' | 'previous'} direction
   */
  #recheck(direction) {
    const el = direction === 'next' ? this.refs.viewMoreNext : this.refs.viewMorePrevious;
    if (!el || !this.infinityScrollObserver) return;
    this.infinityScrollObserver.unobserve(el);
    this.infinityScrollObserver.observe(el);
  }

  async #renderNextPage() {
    if (this.#busyNext) return;
    const page = this.#last + 1;
    if (page > this.#lastPageNumber) return;

    const gen = this.#gen;
    this.#busyNext = true;
    this.renderingPages.add(page);
    let done = false;

    try {
      const html = await this.#loadPage(page);
      if (gen !== this.#gen || !this.isConnected) return;

      const { grid } = this.refs;
      if (!grid) return;

      if (!html) {
        setTimeout(() => this.#recheck('next'), 2500);
        return;
      }

      const fragment = document.createDocumentFragment();
      for (const el of this.#cardsFor(page, html)) fragment.appendChild(el);
      grid.appendChild(fragment);

      this.renderedPages.add(page);
      this.#last = page;
      this.#aspectRatioHelper?.processNewElements();
      this.#prefetch(page + 1);
      this.#prefetch(page + 2);
      done = true;
    } finally {
      this.renderingPages.delete(page);
      if (gen === this.#gen) this.#busyNext = false;
    }

    if (done) this.#recheck('next');
  }

  async #renderPreviousPage() {
    if (this.#busyPrevious || this.isInitialRendering) return;
    const page = this.#first - 1;
    if (page < 1) return;

    const gen = this.#gen;
    this.#busyPrevious = true;
    this.renderingPages.add(page);
    let done = false;

    try {
      const html = await this.#loadPage(page);
      if (gen !== this.#gen || !this.isConnected) return;

      if (!html) {
        setTimeout(() => this.#recheck('previous'), 2500);
        return;
      }

      // Inserting above the screen means correcting the scroll position, which stops a phone's
      // momentum scroll. So wait until the page is at rest, unless the shopper has reached the top.
      await this.#waitForScrollRest();
      if (gen !== this.#gen || !this.isConnected) return;

      this.#prependKeepingPosition(this.#cardsFor(page, html));
      this.renderedPages.add(page);
      this.#first = page;
      this.#prefetch(page - 1);
      done = true;
    } finally {
      this.renderingPages.delete(page);
      if (gen === this.#gen) this.#busyPrevious = false;
    }

    if (done) this.#recheck('previous');
  }

  /** Resolves once scrolling has paused, at once if the top trigger is on screen, or after 5s. */
  #waitForScrollRest() {
    const start = performance.now();
    return new Promise((resolve) => {
      const check = () => {
        const now = performance.now();
        const sentinel = this.refs.viewMorePrevious;
        const rect = sentinel?.getBoundingClientRect();
        const onScreen = rect ? rect.bottom > -50 && rect.top < window.innerHeight : false;
        if (now - this.#lastScrollAt > SCROLL_REST_MS || onScreen || now - start > 5000) resolve(undefined);
        else setTimeout(check, 60);
      };
      check();
    });
  }

  /**
   * Adds cards above the current ones without moving what the shopper sees.
   * @param {Element[]} elements
   */
  #prependKeepingPosition(elements) {
    const { grid } = this.refs;
    if (!grid || !elements.length) return;

    const reference = grid.firstElementChild;
    const before = reference ? reference.getBoundingClientRect().top : 0;

    const fragment = document.createDocumentFragment();
    for (const el of elements) fragment.appendChild(el);
    grid.prepend(fragment);

    if (reference) {
      const shift = reference.getBoundingClientRect().top - before;
      if (Math.abs(shift) > 0.5) window.scrollTo({ top: window.scrollY + shift, behavior: 'instant' });
    }

    this.#aspectRatioHelper?.processNewElements();
  }

  /**
   * Landing on page=N (back button, refresh or a shared link): load the two pages above together
   * and add them in one step. Pages further up follow through #renderPreviousPage as the shopper
   * scrolls up, so a deep page in a big collection does not download the whole collection.
   */
  async #initialRender() {
    if (this.initialRendered) return;
    this.initialRendered = true;

    const current = this.#getCurrentPage();
    if (current <= 1 || !this.refs.grid) return;

    const gen = this.#gen;
    this.isInitialRendering = true;

    try {
      const wanted = [];
      for (let page = Math.max(1, current - RESTORE_PAGES_ABOVE); page < current; page++) wanted.push(page);
      const results = await Promise.all(wanted.map((page) => this.#loadPage(page)));
      if (gen !== this.#gen || !this.isConnected) return;

      // Only add the unbroken run of pages directly above the current one; if a request failed,
      // the pages above the gap load on scroll as before.
      // Each page is about 1 MB of HTML, so parse one per task to keep phones responsive.
      const elements = [];
      let first = current;
      for (let i = results.length - 1; i >= 0; i--) {
        const html = results[i];
        if (!html) break;
        await new Promise((resolve) => setTimeout(resolve, 0));
        if (gen !== this.#gen || !this.isConnected) return;
        elements.unshift(...this.#cardsFromHTML(html));
        first = wanted[i];
      }

      if (elements.length) {
        this.#prependKeepingPosition(elements);
        for (let page = first; page < current; page++) this.renderedPages.add(page);
        this.#first = first;
      }
    } finally {
      if (gen === this.#gen) {
        requestAnimationFrame(() => {
          this.#applyAnchor();
          this.isInitialRendering = false;
          this.#recheck('previous');
        });
      }
    }
  }

  // ---------------------------------------------------------------- address bar and coming back

  /** The card crossing the URL line, found by binary search (cards are in page order). */
  #cardAtLine() {
    const cards = this.#cards();
    if (!cards.length) return null;

    const line = window.innerHeight * URL_LINE;
    let low = 0;
    let high = cards.length - 1;
    let found = cards[cards.length - 1];

    while (low <= high) {
      const mid = (low + high) >> 1;
      const card = cards[mid];
      if (card && card.getBoundingClientRect().bottom > line) {
        found = card;
        high = mid - 1;
      } else {
        low = mid + 1;
      }
    }

    return found || null;
  }

  #onScroll = () => {
    this.#lastScrollAt = performance.now();
    if (this.#scrollTimer) return;
    this.#scrollTimer = window.setTimeout(() => {
      this.#scrollTimer = 0;
      this.#syncUrl();
    }, 200);
  };

  #onUserMove = () => {
    this.#userMoved = true;
  };

  #syncUrl() {
    if (this.isInitialRendering || this.#designMode) return;

    const card = this.#cardAtLine();
    if (!card) return;

    const page = Number(card.getAttribute('data-page')) || 1;
    if (page === this.#urlPage) return;
    this.#urlPage = page;

    const url = new URL(window.location.href);
    if (page <= 1) url.searchParams.delete('page');
    else url.searchParams.set('page', String(page));
    url.hash = '';

    if (url.href !== window.location.href) {
      try {
        history.replaceState(history.state, '', url.toString());
      } catch (error) {
        // Safari throttles replaceState; missing one update is harmless.
      }
    }
  }

  #restoreKey() {
    return window.location.pathname + window.location.search;
  }

  /**
   * @param {Element} card
   */
  #saveRecord(card) {
    let target = card;
    while (target && !target.id) target = /** @type {Element} */ (target.nextElementSibling);
    if (!target) return;

    try {
      sessionStorage.setItem(
        RESTORE_KEY,
        JSON.stringify({ key: this.#restoreKey(), id: target.id, top: Math.round(target.getBoundingClientRect().top) })
      );
    } catch (error) {
      // Private mode or storage blocked: coming back then starts at the top of the page.
    }
  }

  /**
   * Clicking a product: put its page and card in the address bar right away (the browser leaves
   * the page before a delayed update could run) and remember where the card sat on screen.
   * @param {MouseEvent} event
   */
  #onCardClick = (event) => {
    if (this.#designMode || !(event.target instanceof Element)) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
    if (event.target.closest(INTERACTIVE)) return;

    const card = event.target.closest('[ref="cards[]"]');
    if (!card || !this.contains(card)) return;

    const url = new URL(window.location.href);
    const page = Number(card.getAttribute('data-page')) || 1;
    if (page <= 1) url.searchParams.delete('page');
    else url.searchParams.set('page', String(page));
    if (card.id) url.hash = card.id;

    try {
      history.replaceState(history.state, '', url.toString());
    } catch (error) {
      // ignore
    }
    this.#urlPage = page;
    this.#saveRecord(card);
    this.#clickSavedAt = Date.now();
  };

  /** Leaving the page any other way: remember the card in view. */
  #onPageHide = () => {
    if (this.#designMode || Date.now() - this.#clickSavedAt < 10000) return;
    this.#syncUrl();
    const card = this.#cardAtLine();
    if (card && card.getBoundingClientRect().top < window.innerHeight) this.#saveRecord(card);
  };

  /** Back button or refresh: find the card to put back on screen. */
  #restorePosition() {
    if (this.#designMode) return;

    const navigation = /** @type {PerformanceNavigationTiming | undefined} */ (
      performance.getEntriesByType?.('navigation')?.[0]
    );
    const type = navigation?.type;
    if (type !== 'back_forward' && type !== 'reload') return;

    /** @type {{ key: string, id: string, top: number } | null} */
    let record = null;
    try {
      record = JSON.parse(sessionStorage.getItem(RESTORE_KEY) || 'null');
    } catch (error) {
      record = null;
    }

    /** @type {Element | null} */
    let el = null;
    let top = null;

    if (record && record.key === this.#restoreKey()) {
      el = document.getElementById(record.id);
      top = typeof record.top === 'number' ? record.top : null;
    }

    if (!el && window.location.hash) {
      try {
        el = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
      } catch (error) {
        el = null;
      }
    }

    if (!el || !this.contains(el)) return;

    const card = el.closest('[ref="cards[]"]') || el;
    const fallbackTop = Math.max(80, (window.innerHeight - card.getBoundingClientRect().height) / 2);
    this.#anchor = { el: card, top: top ?? fallbackTop };
    this.#applyAnchor();

    // Late layout (fonts, the announcement bar) can still move things: settle once more after load.
    window.addEventListener('load', () => this.#applyAnchor(), { once: true });
  }

  #applyAnchor() {
    const anchor = this.#anchor;
    if (!anchor || this.#userMoved || !anchor.el.isConnected) return;

    const y = anchor.el.getBoundingClientRect().top + window.scrollY - anchor.top;
    window.scrollTo({ top: Math.max(0, y), behavior: 'instant' });
  }

  // ---------------------------------------------------------------- filters

  #handleFilterUpdate = () => {
    this.#gen++;
    this.pages.clear();
    this.#requests.clear();
    this.#parsed.clear();
    this.renderedPages.clear();
    this.renderingPages.clear();
    this.fetchingPages.clear();
    this.#busyNext = false;
    this.#busyPrevious = false;
    this.isInitialRendering = false;
    this.#anchor = null;

    this.infinityScrollObserver?.disconnect();
    this.infinityScrollObserver = undefined;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.#resetRange();
        this.#observeViewMore();
        this.#prefetch(this.#last + 1);
      });
    });
  };
}
